import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  api,
  ApiError,
  streamEvents,
  type AnswerResponse,
  type AnswerSegment,
  type Citation,
  type DocumentListResponse,
  type RetrievalScopeOption,
  type VersionInfo,
} from "../../lib/api";
import { formatTime } from "../../lib/format";
import { copyText } from "../../lib/clipboard";
import { Card, Check, ErrorNote, InfoDot, Spinner, useToast } from "../../components/ui";
import { ChatHistory } from "./ChatHistory";
import {
  blocksToText,
  groupSegments,
  sharedCitation,
  type Block,
} from "./answerBlocks";

/**
 * Compendium Discover (the assistant).
 *
 * Each answer segment is followed by its citation chip, and each citation is a
 * card that opens the document at the cited page — the contract fixed by
 * specification section 5 and assumed by the prototype.
 *
 * A refusal renders as a normal answer, not an error. The system prefers "the
 * compendium does not say" over a fluent answer nobody can trace, so showing
 * that as a failure would misrepresent it.
 */

type Turn = {
  id: string;
  question: string;
  answer?: AnswerResponse;
  /**
   * The answer as far as it has been written, structured server-side from the
   * text so far. Provisional: no emphasis, no citations, and replaced whole by
   * `answer` when generation ends. Absent on a replayed turn, which was never
   * streamed.
   */
  partial?: AnswerSegment[];
  error?: unknown;
  askedAt: string;
};

const SCOPE_INFO: Record<string, { title: string; text: string }> = {
  published: {
    title: "Published documents",
    text: "Documents that are in force. This is the default, and the only scope a read-only role can use.",
  },
  drafts: {
    title: "Drafts and work in progress",
    text: "Documents still moving through review or approval. Anything you are not permitted to open is excluded from retrieval entirely, not merely hidden from the results.",
  },
  archived: {
    title: "Archived versions",
    text: "Superseded versions. Retained so you can prove what was in force on a given date, and excluded from answers unless you enable them here.",
  },
  attachments: {
    title: "Library attachments",
    text: "Files attached to library list items. This needs the SharePoint connector, which is not enabled in this deployment.",
  },
};

export function AssistantScreen() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [threadId, setThreadId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [scopes, setScopes] = useState<string[]>(["published"]);
  const threadRef = useRef<HTMLDivElement>(null);

  const { data: scopeOptions } = useQuery({
    queryKey: ["answer-scopes"],
    queryFn: () => api.get<RetrievalScopeOption[]>("/answers/scopes"),
  });

  // The Answer settings panel reports the live retrieval budget rather than a
  // number typed into the markup, which went stale the moment it was raised.
  const { data: version } = useQuery({
    queryKey: ["version"],
    queryFn: () => api.get<VersionInfo>("/version"),
    staleTime: 5 * 60_000,
  });

  const ask = useMutation({
    mutationFn: async ({
      question,
      verbatim,
      onPartial,
    }: {
      question: string;
      verbatim: boolean;
      onPartial: (segments: AnswerSegment[]) => void;
    }) => {
      let answer: AnswerResponse | null = null;
      for await (const frame of streamEvents("/answers/stream", {
        question,
        threadId,
        scope: scopes,
        verbatim,
      })) {
        if (frame.event === "delta") {
          onPartial((frame.data as { segments: AnswerSegment[] }).segments);
        } else if (frame.event === "restart") {
          // A citation did not resolve and the model is answering again. What
          // was on screen is being replaced, not added to.
          onPartial([]);
        } else if (frame.event === "answer") {
          answer = frame.data as AnswerResponse;
        } else if (frame.event === "error") {
          const { code, message } = frame.data as { code: string; message: string };
          throw new ApiError(500, code, message);
        }
      }
      if (!answer) throw new ApiError(0, "no_answer", "The answer never arrived");
      return answer;
    },
  });

  /** `verbatim` skips follow-up rewriting — the "Ask as typed" escape hatch. */
  const send = (question: string, verbatim = false) => {
    const trimmed = question.trim();
    if (!trimmed || ask.isPending) return;

    const turn: Turn = {
      id: `${Date.now()}`,
      question: trimmed,
      askedAt: new Date().toISOString(),
    };
    setTurns((current) => [...current, turn]);
    setDraft("");

    ask.mutate(
      {
        question: trimmed,
        verbatim,
        // Provisional structure, replaced whole by the `answer` frame. Held on
        // the turn so a second question cannot land in the first one's bubble.
        onPartial: (segments) =>
          setTurns((current) =>
            current.map((existing) =>
              existing.id === turn.id ? { ...existing, partial: segments } : existing,
            ),
          ),
      },
      {
        onSuccess: (answer) => {
          setThreadId(answer.threadId);
          restored.current = answer.threadId;
          setParams({ thread: answer.threadId }, { replace: true });
          setTurns((current) =>
            current.map((t) => (t.id === turn.id ? { ...t, answer } : t)),
          );
          void queryClient.invalidateQueries({ queryKey: ["threads"] });
        },
        onError: (error) => {
          setTurns((current) =>
            current.map((t) => (t.id === turn.id ? { ...t, error } : t)),
          );
        },
      },
    );
  };

  // A question passed in the URL — from the header search or a home-screen
  // suggestion — is asked once. Guarded by a ref rather than the URL alone:
  // StrictMode runs effects twice in development, and clearing the params is a
  // state update that has not landed by the second run.
  const pending = params.get("q");
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (!pending || asked.current === pending) return;
    asked.current = pending;
    send(pending);
    setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending]);

  // Restore the thread named in the URL, once. `restored` guards against
  // StrictMode's double effect run and against re-fetching every time the
  // params object is replaced by an unrelated update.
  const wanted = params.get("thread");
  const restored = useRef<string | null>(null);
  useEffect(() => {
    if (!wanted || restored.current === wanted) return;
    restored.current = wanted;
    void loadThread(wanted).catch(() =>
      setLoadError("That conversation is no longer available."),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted]);

  useEffect(() => {
    const element = threadRef.current;
    if (!element) return;
    requestAnimationFrame(() => {
      element.scrollTop = element.scrollHeight;
    });
  }, [turns, ask.isPending]);

  /** Open a thread and record it in the URL.
   *
   * The thread id lives in the query string rather than in component state
   * alone, so a reload, a bookmark or a shared link lands back on the same
   * conversation instead of an empty pane.
   */
  const openThread = async (id: string) => {
    restored.current = id;
    setLoadError(null);
    try {
      await loadThread(id);
      setParams({ thread: id }, { replace: true });
    } catch {
      setLoadError("That conversation could not be opened.");
    }
  };

  const startNewThread = () => {
    restored.current = null;
    setTurns([]);
    setThreadId(null);
    setLoadError(null);
    setParams({}, { replace: true });
  };

  const loadThread = async (id: string) => {
    const detail = await api.get<{
      id: string;
      title: string;
      messages: {
        id: string;
        role: string;
        question: string | null;
        answer: AnswerSegment[];
        citations: Citation[];
        followUps: string[];
        refused: boolean;
        model: string | null;
        tokensIn: number;
        tokensOut: number;
        latencyMs: number;
        chunksUsed: number;
        createdAt: string;
        resolvedQuestion: string | null;
      }[];
    }>(`/answers/threads/${id}`);

    setTurns(
      detail.messages
        .filter((m) => m.role !== "user")
        .map((message) => ({
          id: message.id,
          question: message.question ?? "",
          askedAt: message.createdAt,
          answer: {
            answer: message.answer,
            citations: message.citations,
            model: message.model ?? "",
            tokensIn: message.tokensIn,
            tokensOut: message.tokensOut,
            latencyMs: message.latencyMs,
            refused: message.refused,
            messageId: message.id,
            threadId: detail.id,
            chunksUsed: message.chunksUsed,
            followUps: message.followUps,
            resolvedQuestion: message.resolvedQuestion,
          },
        })),
    );
    setThreadId(detail.id);
  };

  const toggleScope = (id: string) => {
    setScopes((current) => {
      const next = current.includes(id)
        ? current.filter((s) => s !== id)
        : [...current, id];
      return next.length ? next : ["published"];
    });
  };

  // The placeholder stands down the moment the first words arrive.
  const lastPartial = turns[turns.length - 1]?.partial?.length;

  return (
    // From `lg` the pane has a definite height (see Shell), so the row can
    // take it and each column scrolls inside itself. Below `lg` the columns
    // stack and the pane scrolls, so no height is imposed.
    <div className="flex flex-wrap items-stretch gap-4 lg:h-full lg:flex-nowrap">
      {/* ------------------------------------------------------- chat card */}
      <div className="flex min-h-[480px] flex-1 basis-[440px] flex-col self-stretch overflow-hidden rounded-panel border border-line bg-surface lg:min-h-0">
        <header className="flex shrink-0 items-center gap-[11px] border-b border-line-soft px-[18px] py-[13px]">
          <img
            src="/assets/nxzen-logomark.jpg"
            alt=""
            className="h-7 w-7 rounded-nav object-cover"
          />
          <div className="min-w-0 flex-1">
            <div className="text-md+ font-bold leading-tight">Compendium Discover</div>
            <div className="text-sm text-muted">
              Every claim is cited to the document and page it came from
            </div>
          </div>
          <button className="btn-small" onClick={startNewThread}>
            New thread
          </button>
        </header>

        <div
          ref={threadRef}
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-5"
        >
          {/* Pushes a short thread to the bottom of the card, so the newest
              turn sits near the box you type in. Not before the first one:
              the empty state is the whole content of the pane, and shoving it
              to the floor left eight hundred pixels of nothing above it. */}
          {turns.length > 0 && <div className="flex-1 shrink-0 grow" aria-hidden />}

          {loadError && (
            <div className="mx-auto max-w-md rounded-card border border-danger-line bg-danger-wash px-3 py-2 text-base+ text-danger-deep">
              {loadError}
            </div>
          )}

          {turns.length === 0 && !ask.isPending && (
            <ColdStart onPick={(question) => setDraft(question)} />
          )}

          {turns.map((turn) => (
            <div key={turn.id} className="flex flex-col gap-4">
              <QuestionBubble
                question={turn.question}
                busy={ask.isPending}
                onAskAgain={(edited) => send(edited)}
                onCopied={() => toast("Question copied")}
              />

              {turn.error !== undefined && <ErrorNote error={turn.error} />}

              {!turn.answer && turn.partial && turn.partial.length > 0 && (
                <PartialAnswer segments={turn.partial} />
              )}

              {turn.answer && (
                <AnswerBubble
                  answer={turn.answer}
                  askedAt={turn.askedAt}
                  askedQuestion={turn.question}
                  onFollowUp={send}
                  onOpenCitation={(citation) =>
                    navigate(
                      `/documents/${citation.documentId}?page=${citation.pageFrom}`,
                    )
                  }
                  onCopied={() => toast("Answer copied")}
                  onAskVerbatim={() => send(turn.question, true)}
                />
              )}
            </div>
          ))}

          {ask.isPending && !lastPartial && <Working />}
        </div>

        <footer className="shrink-0 border-t border-line-soft px-[18px] pb-[15px] pt-[13px]">
          {/* One field with the control inside it, rather than a box beside a
              button. The two-element version read as a form; a question is not
              a form, and the grey Ask button sat there looking disabled even
              when it was not. */}
          <div className="flex items-end gap-2 rounded-[14px] border border-line bg-surface-subtle py-[7px] pl-[14px] pr-[7px] transition-colors focus-within:border-violet focus-within:bg-surface">
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  send(draft);
                }
              }}
              placeholder="Ask about a procedure, threshold, or approval route…"
              aria-label="Ask a question"
              className="min-w-0 flex-1 border-0 bg-transparent py-[9px] text-md outline-none placeholder:text-muted-faint"
              disabled={ask.isPending}
            />
            <button
              className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full border-0 transition-all disabled:cursor-default"
              style={
                draft.trim() && !ask.isPending
                  ? { background: "#030304", color: "#F6F2F4" }
                  : { background: "#EDE9EC", color: "#B7B0B5" }
              }
              onClick={() => send(draft)}
              disabled={ask.isPending || !draft.trim()}
              aria-label="Ask"
              title="Ask"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.1"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M12 19V5" />
                <path d="m6 11 6-6 6 6" />
              </svg>
            </button>
          </div>
        </footer>
      </div>

      {/* -------------------------------------------------------- right rail */}
      <div className="flex flex-1 basis-[290px] flex-col gap-3 self-start lg:min-h-0 lg:max-w-[360px] lg:self-stretch lg:overflow-y-auto lg:overscroll-contain">
        <Card
          collapsible
          storageKey="retrieval-scope"
          title="Retrieval scope"
          info={{
            title: "Retrieval scope",
            text: "Anything you are not permitted to open is excluded from retrieval entirely, not merely hidden from the results.",
          }}
        >
          {!scopeOptions && <Spinner />}
          {scopeOptions?.map((option) => {
            const info = SCOPE_INFO[option.id];
            return (
              <div
                key={option.id}
                className={`mb-2 flex items-center gap-[10px] last:mb-0 ${
                  option.available ? "" : "opacity-45"
                }`}
              >
                <Check
                  checked={scopes.includes(option.id)}
                  label={option.label}
                  onChange={() => option.available && toggleScope(option.id)}
                />
                <span className="flex-1 text-base+ font-semibold text-ink">
                  {option.label}
                </span>
                <span className="text-sm text-muted-soft">{option.count}</span>
                {info && <InfoDot title={info.title} text={info.text} />}
              </div>
            );
          })}
        </Card>

        <ChatHistory
          activeId={threadId}
          onOpen={(id) => void openThread(id)}
          onNew={startNewThread}
        />

        <Card title="Answer settings" collapsible storageKey="answer-settings" defaultOpen={false}>
          <SettingRow label="Retriever" value="Hybrid, fused" info={{
            title: "Hybrid retrieval",
            text: "Two searches run at once: a keyword search for exact terms like a document number, and a vector search for meaning, so 'gas leak' also finds 'escape'. The two ranked lists are then fused.",
          }} />
          <SettingRow
            label="Passages per answer"
            value={version ? String(version.retrievalTopK) : "…"}
            info={{
              title: "Passages per answer",
              text: `Documents are split into passages of 300 to 500 tokens. The retriever picks the ${
                version?.retrievalTopK ?? "most"
              } most relevant, and the model may use only those.`,
            }}
          />
          {/* Read from /version rather than written here. Both of these are
              facts about how the system is configured, and a panel whose job
              is to tell a reader how their answer was produced must not state
              them from memory -- an administrator can change the floor, and
              the panel would go on confidently reporting the old one. */}
          <SettingRow
            label="Citations"
            value={version?.citationsRequired === false ? "Optional" : "Required"}
            valueTone={version?.citationsRequired === false ? undefined : "#2F6420"}
            info={{
              title: "Citations required",
              text: "Every claim must point at the passage it came from. A citation that does not resolve causes one regeneration, then a refusal.",
            }}
          />
          <SettingRow
            label="Below the floor"
            value="Refuse"
            info={{
              title: "Relevance floor",
              text: `If nothing relevant is retrieved the assistant declines rather than producing an unsupported answer${
                version ? `, which here means nothing above a similarity of ${version.relevanceFloor}` : ""
              }. A refusal is a correct outcome, not a failure.`,
            }}
          />
        </Card>

        <p className="note-dashed">
          Answers draw only on documents you are permitted to read. Restricted
          items are excluded from retrieval, not merely hidden from results.
        </p>
      </div>
    </div>
  );
}


/**
 * The rendered page a citation came from.
 *
 * Open by default when the passage carries diagram text or the question asked
 * for a picture: a claim taken from a mimic screenshot is far easier to check
 * against the screenshot than against its transcription. Otherwise it is behind
 * a click, because most citations are prose and a page image would be noise.
 *
 * The bytes come through `api.blob` rather than an `<img src>` because the
 * endpoint authenticates with a bearer header. Page images are keyed by
 * version, so `versionId` is passed when the citation has one — without it the
 * endpoint falls back to the document's current version and a citation into an
 * archived version shows the wrong page.
 */
function CitationPage({
  citation,
  defaultOpen,
}: {
  citation: Citation;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!open) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setFailed(false);
    void api
      .blob(`/documents/${citation.documentId}/pages/${citation.pageFrom}`, {
        ...(citation.versionId ? { version_id: citation.versionId } : {}),
      })
      .then((next) => {
        // Revoked immediately if the component went away while the request was
        // in flight, so a fast navigation does not leak the blob.
        if (cancelled) {
          URL.revokeObjectURL(next);
          return;
        }
        objectUrl = next;
        setUrl(next);
      })
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, citation.documentId, citation.pageFrom, citation.versionId]);

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 text-sm+ font-bold text-muted-soft hover:text-ink"
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform duration-150 ${open ? "rotate-90" : ""}`}
          aria-hidden
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
        {open ? "Hide" : "Show"} page {citation.pageFrom}
        {citation.fromDiagram && !open && " · has a diagram"}
      </button>

      {open && (
        <div className="mt-2 overflow-hidden rounded-soft border border-line bg-surface-sunken">
          {failed ? (
            <p className="px-3 py-4 text-center text-sm+ text-muted">
              That page has not been rendered yet.
            </p>
          ) : url ? (
            <img
              src={url}
              alt={`${citation.docNo} page ${citation.pageFrom}`}
              className="block w-full"
            />
          ) : (
            <div className="py-6">
              <Spinner label="Rendering page…" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A segment's text with its emphasised ranges in bold.
 *
 * The ranges arrive as character offsets, already sorted and non-overlapping,
 * so this walks them once. Nothing is parsed and nothing is sanitised: there is
 * no markup in the payload to begin with.
 */
function SegmentText({
  segment,
  boldTo = 0,
}: {
  segment: AnswerSegment;
  boldTo?: number;
}) {
  // A key-value row's label is bolded as *structure*, not as emphasis: the
  // server decided the line is labelled, and the boundary is its first colon.
  // Prepended rather than merged, and the emphasis spans that start inside it
  // are dropped — filtered as well as guarded, so the intent is on the page
  // and not only in the effect.
  const found = segment.emphasis ?? [];
  const ranges: [number, number][] =
    boldTo > 0
      ? [[0, boldTo], ...found.filter(([start]) => start >= boldTo)]
      : found;
  if (ranges.length === 0) return <>{segment.text}</>;

  const parts: ReactNode[] = [];
  let cursor = 0;
  ranges.forEach(([start, end], index) => {
    // Defensive: a range outside the text would mean the offsets were computed
    // against a different string, which is worth ignoring rather than throwing.
    if (start < cursor || end > segment.text.length || start >= end) return;
    if (start > cursor) parts.push(segment.text.slice(cursor, start));
    parts.push(
      <strong key={index} className="font-bold text-ink">
        {segment.text.slice(start, end)}
      </strong>,
    );
    cursor = end;
  });
  if (cursor < segment.text.length) parts.push(segment.text.slice(cursor));
  return <>{parts}</>;
}

/**
 * One answer, laid out as the blocks its segments declare.
 *
 * There is no heuristic here any more. Which lines are steps, which are
 * bullets and which are headings was decided server-side from the markdown the
 * model wrote, so this only draws it.
 */
function AnswerBody({
  blocks,
  citations,
  onOpen,
  shown,
  total,
}: {
  blocks: Block[];
  citations: Citation[];
  onOpen: (n: number) => void;
  /** How many segments to show. The bubble owns the timer, because what
   *  follows the answer waits on the same count. */
  shown: number;
  total: number;
}) {
  const byNumber = new Map(citations.map((c) => [c.n, c]));
  const chip = (n: number) => (
    <CitationChip n={n} citation={byNumber.get(n)} onOpen={onOpen} />
  );
  // A running index across blocks, so the limit is in segments and a block
  // can be half written.
  let seen = 0;

  return (
    <div aria-busy={shown < total}>
      {blocks.map((block, index) => {
        const from = seen;
        seen += block.items.length;
        if (from >= shown) return null;
        const items = block.items.slice(0, shown - from);
        // The caret sits at the end of whatever was written last.
        const tail = seen > shown;
        // When every row of a list cites the same passage, the chip goes once
        // at the end of the list rather than on all eight rows.
        const shared = sharedCitation(block);
        if (block.type === "heading") {
          const segment = block.items[0];
          return (
            // Not a document heading — a label inside a chat bubble. Sized just
            // above body copy rather than as an eyebrow, because it is read as
            // part of the answer and not as chrome.
            <h4
              key={index}
              className="mb-[5px] mt-[15px] text-md+ font-extrabold text-ink first:mt-0"
            >
              <SegmentText segment={segment} />
              {segment.citation !== null && chip(segment.citation)}
            </h4>
          );
        }

        if (block.type === "ordered") {
          return (
            // A procedure rendered as a paragraph is not a procedure. Asked
            // what to do before an End to End test, the assistant returns
            // fourteen separately cited steps -- and inline they ran together
            // into a wall of prose with superscripts scattered through it,
            // which is unusable for the one thing an engineer would do with
            // it: work down the list.
            //
            // `list-none` with the number drawn here rather than by the
            // browser, because the number may be the document's own: an answer
            // quoting steps 4, 5 and 7 has to show 4, 5 and 7.
            <ol
              key={index}
              className="my-[9px] flex list-none flex-col gap-[7px] first:mt-0 last:mb-0"
            >
              {items.map((segment, position) => (
                <li key={position} className="animate-write flex gap-[9px]">
                  {/* Not aria-hidden, unlike the decorative dot below: this is
                      real content, and possibly the document's own numbering. */}
                  <span
                    className="w-[17px] shrink-0 text-right font-extrabold tabular-nums"
                    style={{ color: "#4B2E83" }}
                  >
                    {segment.ordinal ?? position + 1}.
                  </span>
                  <span className="min-w-0 flex-1">
                    <SegmentText segment={segment} />
                    {shared === null && segment.citation !== null && chip(segment.citation)}
                  </span>
                </li>
              ))}
              {shared !== null && !tail && <SharedSource n={shared} render={chip} />}
              {tail && <Caret indent />}
            </ol>
          );
        }

        if (block.type === "unordered") {
          return (
            <ul
              key={index}
              className="my-[9px] flex list-none flex-col gap-[7px] first:mt-0 last:mb-0"
            >
              {items.map((segment, position) => {
                const boldTo =
                  segment.kind === "key_value"
                    ? segment.text.indexOf(":") + 1
                    : 0;
                return (
                  <li key={position} className="animate-write flex gap-[9px]">
                    <span
                      className="mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-violet"
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <SegmentText segment={segment} boldTo={boldTo} />
                      {shared === null && segment.citation !== null && chip(segment.citation)}
                    </span>
                  </li>
                );
              })}
              {shared !== null && !tail && <SharedSource n={shared} render={chip} />}
              {tail && <Caret indent />}
            </ul>
          );
        }

        return (
          <p key={index} className="mb-[9px] last:mb-0">
            {items.map((segment, position) => (
              <span key={position} className="animate-write inline">
                <SegmentText segment={segment} />
                {segment.citation !== null && chip(segment.citation)}{" "}
              </span>
            ))}
            {tail && <Caret />}
          </p>
        );
      })}
    </div>
  );
}

/**
 * The blank chat, before anybody has asked anything.
 *
 * It was a heading and a paragraph floating in eight hundred pixels of nothing,
 * and the paragraph answered the wrong question. A reader arriving here does
 * not need to be told the assistant only uses indexed documents -- they need to
 * know *which* documents, because a question about something that was never
 * ingested comes back as a refusal that reads like a failure.
 *
 * So the empty state is the shelf: what is in the compendium right now, read
 * from the library rather than written here, so it cannot go stale. Clicking
 * one puts a question about it in the box rather than asking it, because the
 * reader's own question is usually a better one than ours.
 */
function ColdStart({ onPick }: { onPick: (question: string) => void }) {
  const { data } = useQuery({
    queryKey: ["cold-start-documents"],
    queryFn: () =>
      api.get<DocumentListResponse>("/documents", {
        status: "PUBLISHED",
        page_size: 6,
        sort: "updated",
      }),
    staleTime: 5 * 60_000,
  });
  const documents = data?.items ?? [];

  return (
    <div className="mx-auto my-auto w-full max-w-[560px] py-6">
      <h2 className="text-center font-display text-h2serif font-normal text-ink">
        Ask about a procedure, threshold or approval route
      </h2>
      <p className="mx-auto mt-2 max-w-md text-center text-base+ leading-[1.65] text-muted">
        Every claim comes back cited to the page it was read from. If the
        compendium does not cover a question, the assistant says so rather than
        guessing.
      </p>

      {documents.length > 0 && (
        <div className="mt-7">
          <div className="mb-2.5 flex items-center gap-2.5">
            <span className="micro-tight whitespace-nowrap">In the compendium</span>
            <span className="h-px flex-1 bg-line-soft" />
            <span className="text-2xs text-muted-soft">{data?.total} published</span>
          </div>
          <ul className="flex flex-col gap-1.5">
            {documents.map((document) => (
              <li key={document.id}>
                <button
                  className="group flex w-full items-center gap-3 rounded-soft border border-line-soft bg-surface-subtle px-3 py-2.5 text-left transition-colors hover:border-violet hover:bg-surface"
                  onClick={() => onPick(`What does ${document.title} say about `)}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-base+ font-bold text-ink">
                      {document.title}
                    </span>
                    <span className="block truncate text-2xs text-muted-soft">
                      {document.docNo} · {document.categoryLabel} ·{" "}
                      {document.pageCount} pages
                    </span>
                  </span>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="shrink-0 text-muted-faint transition-colors group-hover:text-violet"
                    aria-hidden
                  >
                    <path d="M5 12h13" />
                    <path d="m12 6 6 6-6 6" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * The answer as far as it has been written.
 *
 * Structured server-side from the text so far, so a half-written procedure
 * renders as a half-written procedure -- the markdown never reaches here, mid
 * generation or otherwise. It carries no emphasis and no citations because
 * neither exists until the whole completion does, and it is replaced outright
 * by the `answer` frame rather than reconciled with it.
 */
function PartialAnswer({ segments }: { segments: AnswerSegment[] }) {
  const blocks = useMemo(() => groupSegments(segments), [segments]);
  const total = segments.length;
  return (
    <div
      className="max-w-[86%] rounded-[12px] border px-[15px] py-[13px] text-md+ leading-[1.7]"
      style={{ background: "#FFFFFF", borderColor: "#E7E3E6" }}
      aria-busy
    >
      <AnswerBody
        blocks={blocks}
        citations={[]}
        onOpen={() => undefined}
        shown={total}
        total={total + 1}
      />
    </div>
  );
}

/** Where the next words will appear. */
function Caret({ indent = false }: { indent?: boolean }) {
  return (
    <span
      className={`animate-caret inline-block h-[15px] w-[2px] translate-y-[2px] rounded-[1px] ${
        indent ? "ml-[26px]" : "ml-[1px]"
      }`}
      style={{ background: "#4B2E83" }}
      aria-hidden
    />
  );
}

/**
 * The reader's own question, with the two things they want to do to it.
 *
 * **Copy** because a question that produced a good answer is worth keeping --
 * for a colleague, a ticket, or the golden set.
 *
 * **Edit** because the alternative is retyping. Asking a slightly different
 * question is the main loop of using this thing: a term was wrong, or the
 * answer revealed the question was. Editing appends a new turn rather than
 * replacing the old one -- every question asked is an audit record kept for
 * seven years, so a rewrite that quietly removed one would be a rewrite of the
 * record. The original stays above, which is also how a reader sees what
 * changed.
 *
 * The edited question is sent exactly as a typed one is, follow-up rewriting
 * included. Editing changes *what* is asked, not *how* it is read; the answer's
 * own "Ask as typed" button is still there for the other thing.
 */
function QuestionBubble({
  question,
  busy,
  onAskAgain,
  onCopied,
}: {
  question: string;
  busy: boolean;
  onAskAgain: (question: string) => void;
  onCopied: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(question);
  const [copied, setCopied] = useState(false);
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) return;
    const box = field.current;
    if (!box) return;
    box.focus();
    // Caret at the end, not over the text: an edit is almost always an
    // addition or a small change, never a replacement of the whole thing.
    box.setSelectionRange(box.value.length, box.value.length);
    box.style.height = `${box.scrollHeight}px`;
  }, [editing]);

  const cancel = () => {
    setDraft(question);
    setEditing(false);
  };

  const submit = () => {
    const trimmed = draft.trim();
    if (!trimmed || busy) return;
    setEditing(false);
    onAskAgain(trimmed);
  };

  if (editing) {
    return (
      <div className="flex justify-end">
        <div className="w-full max-w-[66%] rounded-[12px] border border-line bg-surface-subtle p-[11px]">
          <textarea
            ref={field}
            value={draft}
            aria-label="Edit the question"
            rows={1}
            className="w-full resize-none bg-transparent text-md+ leading-[1.7] text-ink outline-none"
            onChange={(event) => {
              setDraft(event.target.value);
              event.target.style.height = "auto";
              event.target.style.height = `${event.target.scrollHeight}px`;
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
              if (event.key === "Escape") cancel();
            }}
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button className="btn-tiny" onClick={cancel}>
              Cancel
            </button>
            <button
              className="btn-tiny !bg-ink !text-pearl"
              disabled={!draft.trim() || busy}
              onClick={submit}
            >
              Ask again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="group flex flex-col items-end gap-1">
      <div className="max-w-[66%] rounded-[12px] border border-ink bg-ink px-[15px] py-[13px] text-md+ leading-[1.7] text-pearl">
        {question}
      </div>
      {/* Revealed on hover, and on focus as well -- hover-only controls do not
          exist for anyone using a keyboard. */}
      <div className="flex items-center gap-3 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
        <button
          className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-2xs font-bold text-muted-soft hover:text-ink"
          aria-label="Copy this question"
          onClick={async () => {
            if (!(await copyText(question))) return;
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
            onCopied();
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
          </svg>
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-2xs font-bold text-muted-soft hover:text-ink disabled:opacity-40"
          aria-label="Edit this question and ask it again"
          disabled={busy}
          onClick={() => {
            setDraft(question);
            setEditing(true);
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M4 20h4L19 9a2.5 2.5 0 0 0-3.5-3.5L4.5 16.5z" />
            <path d="m14.5 6.5 3 3" />
          </svg>
          Edit
        </button>
      </div>
    </div>
  );
}

/**
 * What the reader looks at while an answer is being built.
 *
 * It said "Retrieving from the index…" for the whole wait. Retrieval is one or
 * two seconds of it; a twenty-one second answer therefore spent nineteen
 * seconds naming a stage that had finished. The request is a single POST with
 * no progress channel, so the honest correction is not a better sequence of
 * stages -- the browser cannot know which one is running, and advancing them
 * on a timer would be inventing progress. It is to stop claiming a stage, show
 * the one number that is true, and occupy the space the answer will fill so
 * that nothing jumps when it arrives.
 */
function Working() {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const timer = window.setInterval(() => setSeconds((n) => n + 1), 1000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div
      className="max-w-[86%] animate-in rounded-[12px] border px-[15px] py-[13px]"
      style={{ background: "#FFFFFF", borderColor: "#E7E3E6" }}
      role="status"
      aria-live="polite"
      aria-label={`Working through the compendium, ${seconds} seconds`}
    >
      <div className="flex items-center gap-2">
        {/* The same four-point spark the Settings panel uses for generation,
            breathing rather than spinning: a spinner reads as "stuck". */}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
          className="shrink-0 animate-breathe"
          style={{ color: "#4B2E83" }}
          aria-hidden
        >
          <path d="M12 3.2 13.8 9l5.8 1.8-5.8 1.8L12 18.4l-1.8-5.8L4.4 10.8 10.2 9z" />
        </svg>
        <span
          className="animate-sheen bg-clip-text text-base+ font-bold text-transparent"
          style={{
            backgroundImage:
              "linear-gradient(90deg,#A49DA8 0%,#A49DA8 35%,#030304 50%,#A49DA8 65%,#A49DA8 100%)",
            backgroundSize: "200% 100%",
          }}
        >
          Working through the compendium
        </span>
        <span className="ml-auto shrink-0 text-2xs tabular-nums text-muted-soft">
          {seconds}s
        </span>
      </div>

      {/* Three lines where three lines are about to be. The point is not
          decoration: the bubble is already the size of an answer, so the
          transcript does not jump when one arrives. */}
      <div className="mt-[11px] flex flex-col gap-[7px]" aria-hidden>
        {["100%", "94%", "62%"].map((width, index) => (
          <span
            key={width}
            className="animate-sheen block h-[9px] rounded-full"
            style={{
              width,
              backgroundImage:
                "linear-gradient(90deg,#F1EEF3 0%,#F1EEF3 40%,#E3DDE7 50%,#F1EEF3 60%,#F1EEF3 100%)",
              backgroundSize: "200% 100%",
              animationDelay: `${index * 0.12}s`,
            }}
          />
        ))}
      </div>

      {seconds >= 15 && (
        // Said once it is true, rather than pre-emptively. A long procedure is
        // a long answer, and an engineer who knows that waits; one who does
        // not, reloads.
        <p className="mt-[11px] text-2xs text-muted-soft">
          A long procedure takes longer to assemble.
        </p>
      )}
    </div>
  );
}

/** One chip for a whole list, said in words so it cannot read as the last row's. */
function SharedSource({
  n,
  render,
}: {
  n: number;
  render: (n: number) => ReactNode;
}) {
  return (
    <li className="ml-[26px] list-none text-2xs text-muted-soft">
      All of the above{render(n)}
    </li>
  );
}

/**
 * The superscript citation marker that follows the claim it supports.
 *
 * A button, not a decoration. It was a bare `<sup>` holding a number: nothing
 * in an answer was reachable by keyboard, and a screen reader announced a list
 * of eight colour bands as "twelve" eight times. Now it names its source and
 * takes you to it — which is also the thing a reader wanted to do with it.
 */
function CitationChip({
  n,
  citation,
  onOpen,
}: {
  n: number;
  citation?: Citation;
  onOpen?: (n: number) => void;
}) {
  const label = citation
    ? `Source ${n}: ${citation.docNo}, ${citation.locator}`
    : `Source ${n}`;
  return (
    <sup className="ml-[3px] align-super">
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={() => onOpen?.(n)}
        className="rounded-[4px] px-1 py-px text-2xs font-extrabold transition-colors hover:brightness-95"
        style={{ background: "#EFE9F8", color: "#4B2E83" }}
      >
        {n}
      </button>
    </sup>
  );
}

function SettingRow({
  label,
  value,
  valueTone,
  info,
}: {
  label: string;
  value: string;
  valueTone?: string;
  info: { title: string; text: string };
}) {
  return (
    <div className="mb-2.5 flex items-center gap-2 last:mb-0">
      <span className="flex-1 text-base+ text-muted-strong">{label}</span>
      <span className="text-base+ font-bold" style={{ color: valueTone ?? "#030304" }}>
        {value}
      </span>
      <InfoDot title={info.title} text={info.text} />
    </div>
  );
}

function AnswerBubble({
  answer,
  askedAt,
  askedQuestion,
  onFollowUp,
  onOpenCitation,
  onCopied,
  onAskVerbatim,
}: {
  answer: AnswerResponse;
  askedAt: string;
  askedQuestion: string;
  onFollowUp: (question: string) => void;
  onOpenCitation: (citation: Citation) => void;
  onCopied: () => void;
  onAskVerbatim: () => void;
}) {
  const [verdict, setVerdict] = useState<"UP" | "DOWN" | null>(null);
  const [copied, setCopied] = useState(false);

  const blocks = useMemo(() => groupSegments(answer.answer), [answer.answer]);
  const total = blocks.reduce((count, block) => count + block.items.length, 0);
  // Copied with its structure and its sources. Pasted into an incident log,
  // `join(" ")` gave back the same wall of prose the screen no longer shows.
  const plain = useMemo(
    () => blocksToText(blocks, answer.citations),
    [blocks, answer.citations],
  );

  // A reader who asked to see something wants the page open without a second
  // click. Matched on the question rather than the answer, because it is the
  // asking that expresses the intent.
  const wantsPicture =
    /(diagram|figure|screenshot|mimic|colour|color|table|image|picture|show me|look like)/i.test(
      askedQuestion,
    );

  const rate = async (next: "UP" | "DOWN") => {
    setVerdict(next);
    try {
      await api.post(`/answers/messages/${answer.messageId}/feedback`, { verdict: next });
    } catch {
      setVerdict(null);
    }
  };

  return (
    <div className="flex max-w-[86%] flex-col gap-[9px]">
      {/* The reader typed a few words and the system answered a different
          question. That has to be visible, and it has to be reversible — a
          disclosure you cannot act on is just bad news. The reader's own bubble
          above is never altered: replacing what someone typed with what the
          system decided they meant is the one thing this must not do. */}
      {answer.resolvedQuestion && (
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-soft border border-line-soft bg-surface-subtle px-3 py-2">
          <span className="micro-tight shrink-0">Read as</span>
          <span className="min-w-0 flex-1 text-base+ italic leading-[1.55] text-muted-strong">
            “{answer.resolvedQuestion}”
          </span>
          <button className="btn-tiny shrink-0" onClick={onAskVerbatim}>
            Ask as typed
          </button>
        </div>
      )}
      <div
        className="rounded-[12px] border px-[15px] py-[13px] text-md+ leading-[1.7]"
        style={
          answer.refused
            ? { background: "#FCF6D2", borderColor: "#EFE4A8", color: "#030304" }
            : { background: "#FFFFFF", borderColor: "#E7E3E6", color: "#030304" }
        }
      >
        <AnswerBody
          shown={total}
          total={total}
          blocks={blocks}
          citations={answer.citations}
          onOpen={(n) => {
            const found = answer.citations.find((c) => c.n === n);
            if (found) onOpenCitation(found);
          }}
        />
      </div>

      {answer.citations.length > 0 && (
        <div className="animate-in">
          <div className="micro-tight mb-1.5">Sources</div>
          <ul className="flex flex-col gap-1.5">
            {answer.citations.map((citation) => (
              <li
                key={citation.n}
                className="rounded-soft border border-line bg-surface-subtle px-3 py-[10px] transition-colors hover:border-violet"
              >
                <div className="flex items-start gap-[10px]">
                  <span
                    className="grid h-5 w-5 shrink-0 place-items-center rounded-chip text-micro font-extrabold"
                    style={{ background: "#EFE9F8", color: "#4B2E83" }}
                  >
                    {citation.n}
                  </span>
                  <div className="min-w-0 flex-1">
                    {/* Only the heading opens the document, so the page toggle
                        below is reachable — a button cannot nest in a button. */}
                    <button
                      onClick={() => onOpenCitation(citation)}
                      className="block w-full text-left"
                    >
                      <span className="block text-base+ font-bold text-ink">
                        {citation.docNo} {citation.title}
                      </span>
                      <span className="mt-0.5 block text-sm+ text-muted">
                        {citation.locator}
                      </span>
                      <span className="mt-[5px] block text-base italic leading-[1.55] text-muted-strong">
                        “{citation.snippet}”
                      </span>
                    </button>
                    <CitationPage
                      citation={citation}
                      defaultOpen={citation.fromDiagram || wantsPicture}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-[10px]">
        <button
          className="inline-flex items-center gap-1.5 border-0 bg-transparent p-0 text-sm+ font-bold text-muted-soft hover:text-ink"
          onClick={async () => {
            if (!(await copyText(plain))) return;
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
            onCopied();
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M5 15V5a2 2 0 0 1 2-2h10" />
          </svg>
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          className="border-0 bg-transparent p-0 hover:opacity-80"
          onClick={() => void rate("UP")}
          aria-label="This answer was useful"
          style={{ color: verdict === "UP" ? "#2F6420" : "#A49DA8" }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <path d="M7 22V11l5-9a2.5 2.5 0 0 1 2.4 3.2L13.5 9H19a2 2 0 0 1 2 2.4l-1.6 8A2 2 0 0 1 17.4 21H7Z" />
          </svg>
        </button>
        <button
          className="border-0 bg-transparent p-0 hover:opacity-80"
          onClick={() => void rate("DOWN")}
          aria-label="This answer was not useful"
          style={{ color: verdict === "DOWN" ? "#B23438" : "#A49DA8" }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
            <path d="M17 2v11l-5 9a2.5 2.5 0 0 1-2.4-3.2L10.5 15H5a2 2 0 0 1-2-2.4l1.6-8A2 2 0 0 1 6.6 3H17Z" />
          </svg>
        </button>
        <span className="text-sm+ text-muted-faint">{formatTime(askedAt)}</span>
        {answer.citations.length > 0 && (
          <span className="text-sm text-muted-faint">
            · {answer.model} · {(answer.latencyMs / 1000).toFixed(1)}s ·{" "}
            {(answer.tokensIn + answer.tokensOut).toLocaleString("en-GB")} tokens ·{" "}
            {answer.chunksUsed} passages used
          </span>
        )}
      </div>

      {answer.followUps.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {answer.followUps.map((question) => (
            <button
              key={question}
              onClick={() => onFollowUp(question)}
              className="rounded-full border border-line bg-surface px-3 py-1.5 text-base text-muted-strong transition-colors hover:border-ink hover:text-ink"
            >
              {question}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
